import { DRUG_FILTER_LABELS, DRUG_FILTERS } from '@/entities/drug/api/drug.api';
import { usePharmaCompanies } from '@/entities/drug/hooks/useDrugQueries';
import { Button } from '@/shared/components/ui/button';
import { CheckboxField } from '@/shared/components/ui/checkbox';
import { Icon } from '@/shared/components/ui/icon';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/utils';
import { useId, useState, type ReactNode } from 'react';
import { EMPTY_FILTER, type DrugFilterValue } from './drugFilter';

const toggle = <T,>(list: T[], item: T) =>
  list.includes(item) ? list.filter((v) => v !== item) : [...list, item];

/**
 * M-11 필터 (Figma container/left 804:4517) — 필터 · [초기화] / 거래 가능한 의약품만 / 의약품 10종 / 제약사.
 * 의약품 · 제약사는 `검색_필터 타이틀`(823:5009)로 접고 편다.
 * 데스크톱은 바꾸는 즉시 onChange로 URL을 갱신하고, 모바일 Sheet는 임시 값으로 받아 [필터 적용] 때 반영한다.
 */
export const DrugFilterRail = ({
  keyword,
  value,
  onChange,
  className,
}: {
  /** 제약사 목록 조회 키워드 (현재 검색어) */
  keyword: string;
  value: DrugFilterValue;
  onChange: (next: DrugFilterValue) => void;
  className?: string;
}) => {
  // 선택지는 검색어 · 약품 필터로만 정한다 (선택한 제약사 · 거래 가능 체크로 좁히지 않음)
  const { data: companies, isPending } = usePharmaCompanies(
    keyword,
    value.filters,
  );

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="flex items-center justify-between">
        <h2 className="text-body-15-sb text-fg-primary">필터</h2>
        <Button
          variant="text"
          size="small"
          className="text-fg-tertiary"
          onClick={() => onChange(EMPTY_FILTER)}
        >
          초기화
        </Button>
      </div>
      <Divider />
      <CheckboxField
        label="거래 가능한 의약품만"
        checked={value.tradable}
        onCheckedChange={(v) => onChange({ ...value, tradable: v === true })}
      />
      <Divider />
      <FilterSection title="의약품">
        {DRUG_FILTERS.map((f) => (
          <CheckboxField
            key={f}
            label={DRUG_FILTER_LABELS[f]}
            checked={value.filters.includes(f)}
            onCheckedChange={() =>
              onChange({ ...value, filters: toggle(value.filters, f) })
            }
          />
        ))}
      </FilterSection>
      <Divider />
      <FilterSection title="제약사">
        {keyword && isPending ? (
          Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-6 w-32" />
          ))
        ) : companies?.length ? (
          companies.map((c) => (
            <CheckboxField
              key={c.pharmaCompanyId}
              label={c.pharmaCompanyName}
              checked={value.pharma.includes(c.pharmaCompanyId)}
              onCheckedChange={() =>
                onChange({
                  ...value,
                  pharma: toggle(value.pharma, c.pharmaCompanyId),
                })
              }
            />
          ))
        ) : (
          <p className="text-body-14-r text-fg-light">
            선택할 수 있는 제약사가 없어요.
          </p>
        )}
      </FilterSection>
    </div>
  );
};

const Divider = () => <hr className="border-line-grey" />;

/** 검색_필터 타이틀 (823:5009) — 18 Bold 제목 + 접기/펴기 아이콘 버튼(32) · 목록 간격 24 · 항목 간격 12 */
const FilterSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(true);
  const listId = useId();
  return (
    <section className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h3 className="text-subtitle-18-b text-fg-primary">{title}</h3>
        <Button
          variant="text"
          size="small"
          aria-label={`${title} 필터 ${open ? '접기' : '펴기'}`}
          aria-expanded={open}
          aria-controls={listId}
          leftIcon={
            <Icon
              name={open ? 'nav-arrow-up-line-16' : 'nav-arrow-down-line-16'}
              className="text-fg-tertiary"
            />
          }
          onClick={() => setOpen((v) => !v)}
        />
      </div>
      {open && (
        <div id={listId} className="flex flex-col gap-3">
          {children}
        </div>
      )}
    </section>
  );
};
